import {throttle} from 'lodash';

import {ANNOTATION_MARK_ATTRIBUTE} from '../engine/annotationMarkElements';
import {announceAnnotationChange} from './AnnotationAnnouncer';

/** Set on the frozen question-area element; the annotation cursor CSS keys off it. */
export const ANNOTATION_FROZEN_ATTRIBUTE = 'data-annotation-frozen';

/** Marks a subtree the shield leaves interactive (navigation/submit, pending F4). */
export const ANNOTATION_SHIELD_EXEMPT_ATTRIBUTE = 'data-annotation-shield-exempt';

export const ANNOTATION_SHIELD_EXEMPT_PROPS = {[ANNOTATION_SHIELD_EXEMPT_ATTRIBUTE]: ''} as const;

/** Whether the event targets a subtree the shield leaves interactive. */
export function isShieldExempt(event: {target: EventTarget | null}): boolean {
  return (
    event.target instanceof Element &&
    event.target.closest(`[${ANNOTATION_SHIELD_EXEMPT_ATTRIBUTE}]`) !== null
  );
}

/** Id of the reason a locked answer group describes itself by. */
export const LOCKED_ANSWERS_NOTICE_ID = 'annotation-locked-answers';

/** Spread onto a locked answer control: unavailable to assistive tech, still reachable. */
export const lockedControlProps = (isLocked: boolean): {'aria-disabled'?: true} =>
  isLocked ? {'aria-disabled': true} : {};

/** Spread onto a group of locked controls, so the reason is read on entering it. */
export const lockedGroupProps = (
  isLocked: boolean,
  describedBy?: string
): {'aria-describedby'?: string} => {
  const ids = [describedBy, isLocked ? LOCKED_ANSWERS_NOTICE_ID : undefined]
    .filter(Boolean)
    .join(' ');
  return ids ? {'aria-describedby': ids} : {};
};

const LOCKED_ATTEMPT_MESSAGE = 'Locked. Turn off Highlights & Notes to choose an answer.';

const announceLocked = throttle(() => announceAnnotationChange(LOCKED_ATTEMPT_MESSAGE), 1000, {
  trailing: false
});

/**
 * A blocked key, text input, or assistive-tech activation (`detail` 0) on a locked control. Mouse
 * clicks select text, chords are browser commands, and marks open their own editor: none count.
 */
function isAnswerAttempt(event: Event): boolean {
  const {target} = event;
  if (!(target instanceof Element) || !target.closest('[aria-disabled="true"]')) {
    return false;
  }
  if (target.closest(`mark[${ANNOTATION_MARK_ATTRIBUTE}]`)) {
    return false;
  }
  if (event instanceof MouseEvent) {
    return event.detail === 0;
  }
  if (event instanceof KeyboardEvent) {
    return !(event.ctrlKey || event.metaKey || event.altKey || event.getModifierState(event.key));
  }
  return true;
}

const announcingLockedAttempt =
  (block: (event: Event) => boolean): EventListener =>
  (event) => {
    if (block(event) && isAnswerAttempt(event)) {
      announceLocked();
    }
  };

function blockPropagation(event: Event): boolean {
  if (isShieldExempt(event)) {
    return false;
  }
  event.stopPropagation();
  return true;
}

function blockPropagationAndDefault(event: Event): boolean {
  if (isShieldExempt(event)) {
    return false;
  }
  event.stopPropagation();
  event.preventDefault();
  return true;
}

// Tab: no focus trap; Escape: the popover's dismiss listens on document.
const PASS_THROUGH_KEYS = new Set(['Tab', 'Escape']);

function blockKey(event: Event): boolean {
  if (
    isShieldExempt(event) ||
    (event instanceof KeyboardEvent && PASS_THROUGH_KEYS.has(event.key))
  ) {
    return false;
  }
  event.stopPropagation();
  return true;
}

// Never cancelled, so declared passive — keeps native scrolling on the fast path.
const PASSIVE_EVENTS = new Set(['touchstart', 'wheel']);

/**
 * preventDefault only where the default action itself answers; plain stops keep
 * selection, focus, and scrolling alive. `mouseup` is absent — annotation capture.
 */
const SHIELDED_EVENTS: ReadonlyArray<[string, EventListener]> = [
  ['click', announcingLockedAttempt(blockPropagationAndDefault)],
  ['dblclick', blockPropagation],
  ['mousedown', blockPropagation],
  ['pointerdown', blockPropagation],
  ['touchstart', blockPropagation],
  ['keydown', announcingLockedAttempt(blockKey)],
  ['keypress', blockKey],
  ['keyup', blockKey],
  ['beforeinput', announcingLockedAttempt(blockPropagationAndDefault)],
  // insertCompositionText beforeinput isn't cancelable; cancel the IME session at start.
  ['compositionstart', blockPropagationAndDefault],
  ['paste', blockPropagationAndDefault],
  ['cut', blockPropagationAndDefault],
  ['drop', blockPropagationAndDefault],
  ['submit', blockPropagationAndDefault],
  ['wheel', blockPropagation]
];

/**
 * Capture-phase by construction: descendant and document-level handlers never fire first.
 * A fresh freeze speaks its first blocked attempt at once.
 */
export function attachShield(element: HTMLElement): void {
  announceLocked.cancel();
  SHIELDED_EVENTS.forEach(([type, listener]) => {
    element.addEventListener(type, listener, {capture: true, passive: PASSIVE_EVENTS.has(type)});
  });
}

export function detachShield(element: HTMLElement): void {
  SHIELDED_EVENTS.forEach(([type, listener]) => {
    element.removeEventListener(type, listener, {capture: true});
  });
}
